import React, { useMemo, useState } from 'react';
import type { SlotViewerComponentProps } from '@wix/ricos';
import { HttpClient, useBi, useTranslation } from '@wix/yoshi-flow-editor';
import {
  createLoadForms,
  createSubmitForm,
  getFormSlotDesignStyle,
  toFormsI18n,
} from '@wix/communities-blog-client-common';
import { FormPlaceholder } from '@wix/communities-blog-client-common/dist/esm/components';
import { useActions, useSelector } from '@app/external/common/components/runtime-context';
import {
  getInstance,
  getRegionalSettings,
  isSite,
} from '@app/external/common/store/basic-params/basic-params-selectors';
import { getInstanceValue } from '@app/external/common/store/instance-values/instance-values-selectors';
import { getPreloadedForm } from '@app/external/post-page/selectors/preloaded-forms-selectors';
import { FormRenderer } from './form-renderer';
import styles from './form-slot-block.scss';

const createFillSessionId = (nodeId: string): string =>
  globalThis.crypto?.randomUUID?.() ?? `${nodeId}-${Date.now()}`;

/**
 * `Component` of the viewer-side Wix Forms slot integration on the live site.
 *
 * This wrapper ships with the ricos viewer, like every other plugin's block; the Wix Forms renderer
 * behind it is a browser-only lazy chunk. So the server ships the block with a sized placeholder for the form the controller
 * preloaded (an empty loading box when the preload missed), and the client swaps in the real fields
 * inside the same block. Renderer failures go to ricos' recoverable `onError` channel, which the post
 * viewer reports. In the Wix Editor and Preview the form is inert, so nobody creates a real
 * submission while designing the page. The widget signs its HTTP client with the app instance.
 * CAPTCHA runs in the controller, with its token returned through the promisified-action bridge.
 */
const FormSlotViewerBlock: React.FC<SlotViewerComponentProps> = ({
  nodeId,
  resourceId: formId,
  design,
  isMobile,
  locale,
  languageDir,
  onError,
}) => {
  const { t, i18n } = useTranslation();
  const bi = useBi();
  const instance = useSelector(getInstance);
  const isLiveSite = useSelector(isSite);
  const regionalFormat = useSelector(getRegionalSettings);
  const visitorId = useSelector(
    (state) => getInstanceValue(state, 'aid') || getInstanceValue(state, 'uid'),
  );
  const { requestFormCaptcha } = useActions();
  const preloadedForm = useSelector((state) =>
    formId ? getPreloadedForm(state, formId) : undefined,
  );
  const [fillSessionId] = useState(() => createFillSessionId(nodeId));
  // The document's theme reaches the form through `--ricos-*` variables in the stylesheet; this
  // carries the design stored on the node, which ricos hands over already resolved.
  const style = useMemo(() => getFormSlotDesignStyle(design), [design]);

  const host = useMemo(() => {
    const httpClient = new HttpClient({ getAppToken: () => instance });
    return {
      submitForm: createSubmitForm(httpClient, requestFormCaptcha),
      loadForms: createLoadForms(httpClient),
    };
  }, [instance, requestFormCaptcha]);

  if (!formId) {
    return null;
  }

  return (
    <div
      className={styles.formBlock}
      data-hook="forms-slot-block"
      data-form-id={formId}
      style={style}
    >
      <FormRenderer
        key={formId}
        // What the server renders, and what the client shows until the renderer chunk arrives.
        // Mirrors the renderer's own loading states so the swap does not move the page.
        fallback={
          preloadedForm ? (
            <FormPlaceholder form={preloadedForm.form} />
          ) : (
            <div data-hook="forms-slot-loading" aria-busy="true" style={{ minHeight: 160 }} />
          )
        }
        formId={formId}
        compId={nodeId}
        mode={isLiveSite ? 'viewer' : 'editor'}
        isMobile={isMobile}
        direction={languageDir}
        locale={locale}
        regionalFormat={regionalFormat}
        i18n={toFormsI18n(i18n)}
        submitForm={host.submitForm}
        loadForms={host.loadForms}
        preloadedForm={preloadedForm}
        unavailableLabel={t('forms-slot.form-unavailable')}
        bi={bi}
        visitorId={visitorId || undefined}
        biFormFillSessionId={fillSessionId}
        onError={onError}
      />
    </div>
  );
};

export default FormSlotViewerBlock;
